import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';

const allowedCharactersDefaultPattern = "a-zA-Z0-9 äÄãÃåÅáÁàÀâÂéÉèÈêËëöÖóÓòÒôÔõÕüÜúÚùÙûÛìÌíÍîÎïÏñÑøØçÇß',-_.";

interface SearchTranslations {
    requestFailed?: string;
    inputTooShort?: string;
    inputDangerous?: string;
    inputDisallowed?: string;
    noResults?: string;
}

/**
 * TKSearch Web Component for implementing direct search functionality.
 *
 * This component provides a comprehensive search solution with real-time
 * auto-suggestions, input validation, and async HTML content loading.
 * It communicates with an OC (Output Controller) or RD (Report Daemon) endpoint
 * that returns HTML markup containing search results.
 *
 * @class TKSearch
 * @extends {TKCustomElementFactory}
 *
 * @example
 * // HTML Usage
 * <tk-search
 *     fetch-url="{gGenerateURL(search-asyn)}"
 *     search-min-input-length="2"
 *     allowed-characters="[a-zA-Z0-9äöüß\s\-_',.]">
 *     <form data-tk-search>
 *         <input type="text" name="query" />
 *         <select data-tk-search-filter name="filter">
 *             <option value="">All</option>
 *         </select>
 *     </form>
 *     <div data-tk-search-result></div>
 *     <button data-tk-search-back>Back</button>
 *     <button data-tk-search-reset>Reset</button>
 *     <script type="application/json" data-tk-search-translations>
 *         { "requestFailed": "Search failed. Please try again." }
 *     </script>
 * </tk-search>
 *
 * @fires tk-search:loading - Emitted when search request starts
 * @fires tk-search:loaded - Emitted when search results are loaded
 * @fires tk-search:error - Emitted when an error occurs
 * @fires tk-search:reset - Emitted when search is reset
 *
 * @property {string} fetchUrl - URL for fetching search results
 * @property {number} minInputLength - Minimum input length for search
 * @property {RegExp} allowedCharactersRegex - Regex pattern for allowed characters
 * @property {HTMLFormElement} searchForm - Search form element
 * @property {HTMLElement} resultContainer - Container for search results
 */
export default class TKSearch extends TKCustomElementFactory {
    // DOM Elements
    searchForm?: HTMLFormElement;
    resultContainer?: HTMLElement;
    backButton?: HTMLElement;
    resetButton?: HTMLElement;
    filterSelect?: HTMLSelectElement;
    searchInput?: HTMLInputElement;

    // Configuration
    fetchUrl: string;
    minInputLength: number;
    allowedCharactersRegex: RegExp;
    translations: SearchTranslations = {};

    // State
    debounceTimer?: number;
    debounceDelay: number = 500;
    isLoading: boolean = false;
    currentQuery: string = '';
    clickOutsideListener?: (event: MouseEvent) => void;
    searchRequestController?: AbortController;
    searchRequestId: number = 0;

    /**
     * Initialize the component by reading attributes and caching DOM references.
     */
    constructor() {
        super();

        // Read and validate fetch-url attribute
        this.fetchUrl = this.getDataAttribute('fetch-url', '');
        if (!this.fetchUrl) {
            console.warn('TKSearch: Missing required "fetch-url" attribute');
        }

        // Read search-min-input-length attribute (default: 3)
        this.minInputLength = Number(this.getDataAttribute('search-min-input-length', '3'));

        // Read allowed-characters regex attribute
        // The value is interpreted as a character-class body, not a full regex.
        const allowedCharactersPattern = this.getDataAttribute('allowed-characters', allowedCharactersDefaultPattern);
        try {
            const normalizedAllowedCharactersPattern = this.normalizeAllowedCharactersPattern(allowedCharactersPattern);
            this.allowedCharactersRegex = new RegExp(`^[${normalizedAllowedCharactersPattern}]*$`);
        } catch (error) {
            console.error('TKSearch: Invalid regex pattern in "allowed-characters" attribute', error);
            this.allowedCharactersRegex = new RegExp(`^[${allowedCharactersDefaultPattern}]*$`);
        }

        const translationsScript = this.querySelector<HTMLScriptElement>('[data-tk-search-translations]');
        if (translationsScript?.textContent) {
            try {
                this.translations = JSON.parse(translationsScript.textContent) as SearchTranslations;
            } catch (error) {
                console.error('TKSearch: Invalid translations JSON', error);
            }
        }

        // Cache DOM elements
        this.searchForm = this.querySelector('[data-tk-search]') || undefined;
        this.resultContainer = this.querySelector('[data-tk-search-result]') || undefined;
        this.backButton = this.querySelector('[data-tk-search-back]') || undefined;
        this.resetButton = this.querySelector('[data-tk-search-reset]') || undefined;
        this.filterSelect = this.querySelector('[data-tk-search-filter]') || undefined;
        this.searchInput = this.searchForm?.querySelector('input[type="search"]') || undefined;
    }

    /**
     * Lifecycle hook: Called when the component is inserted into the DOM.
     * Validates required elements and registers event listeners.
     */
    connectedCallback(): void {
        if (!this.searchForm || !this.resultContainer || !this.searchInput) {
            throw new Error(
                'TKSearch: Required elements missing ([data-tk-search], [data-tk-search-result], or input field)',
            );
        }

        this.registerEventListeners();
    }

    /**
     * Lifecycle hook: Called when the component is removed from the DOM.
     * Cleans up all event listeners and timers.
     */
    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.clearDebounceTimer();
        this.cancelActiveSearch();

        if (this.clickOutsideListener) {
            document.removeEventListener('click', this.clickOutsideListener);
        }
    }

    /**
     * Registers all required event listeners for the component.
     * Includes: input field, filter select, form submit, reset/back buttons, and click-outside detection.
     *
     * @protected
     */
    protected registerEventListeners(): void {
        // Input field listeners
        if (this.searchInput) {
            this.pushListener({
                event: 'input',
                element: this.searchInput,
                action: this.onSearchInput.bind(this),
            });

            this.pushListener({
                event: 'focus',
                element: this.searchInput,
                action: this.onSearchFocus.bind(this),
            });

            this.pushListener({
                event: 'blur',
                element: this.searchInput,
                action: this.onSearchBlur.bind(this),
            });
        }

        // Filter select listener
        if (this.filterSelect) {
            this.pushListener({
                event: 'change',
                element: this.filterSelect,
                action: this.onFilterChange.bind(this),
            });
        }

        // Form submit listener
        if (this.searchForm) {
            this.pushListener({
                event: 'submit',
                element: this.searchForm,
                action: this.onFormSubmit.bind(this),
            });
        }

        // Reset button listener
        if (this.resetButton) {
            this.pushListener({
                event: 'click',
                element: this.resetButton,
                action: this.onReset.bind(this),
            });
        }

        // Back button listener
        if (this.backButton) {
            this.pushListener({
                event: 'click',
                element: this.backButton,
                action: this.onBack.bind(this),
            });
        }

        // Click outside listener for closing search results
        this.clickOutsideListener = this.onClickOutside.bind(this);
        document.addEventListener('click', this.clickOutsideListener);
    }

    /**
     * Handles input event from the search input field.
     * Validates input, updates state classes, and triggers debounced search.
     *
     * @protected
     * @param {Event} event - The input event
     */
    protected onSearchInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        const query = target.value.trim();

        this.currentQuery = query;
        this.updateStateClasses();

        // Clear previous debounce timer
        this.clearDebounceTimer();
        this.cancelActiveSearch();

        // If query is empty, clear results and return
        if (!query) {
            this.clearResults();
            this.dispatchEvent(new CustomEvent('tk-search:reset'));
            return;
        }

        // Validate input
        const validation = this.validateInput(query);
        if (!validation.isValid) {
            this.displayError(validation.errorMessage, validation.key === 'inputTooShort' ? 'info' : 'warning');
            return;
        }

        this.clearResults();

        // Set debounce timer for search request
        this.debounceTimer = window.setTimeout(() => {
            this.performSearch(query);
        }, this.debounceDelay);
    }

    /**
     * Handles focus event from the search input field.
     * Adds the "active" modifier class.
     *
     * @protected
     * @param {FocusEvent} _event - The focus event
     */
    protected onSearchFocus(_event: FocusEvent): void {
        this.classList.add('tk-search--active');
    }

    /**
     * Handles blur event from the search input field.
     * Can remove the "active" modifier if needed (optional behavior).
     *
     * @protected
     * @param {FocusEvent} _event - The blur event
     */
    protected onSearchBlur(_event: FocusEvent): void {
        // Keep active state for now, will be handled by click-outside
    }

    /**
     * Handles change event from the filter select dropdown.
     * Triggers a new search with the current query when filter changes.
     *
     * @protected
     * @param {Event} _event - The change event
     */
    protected onFilterChange(_event: Event): void {
        // If no current query, don't trigger search
        if (!this.currentQuery) {
            return;
        }

        // Clear previous debounce timer
        this.clearDebounceTimer();
        this.cancelActiveSearch();

        // Trigger new search with new filter value
        this.debounceTimer = window.setTimeout(() => {
            this.performSearch(this.currentQuery);
        }, this.debounceDelay);
    }

    /**
     * Handles form submit event.
     * Validates the search query and navigates to the form action URL if valid.
     * If validation fails, prevents submission and displays error message.
     *
     * @protected
     * @param {SubmitEvent} event - The submit event
     */
    protected onFormSubmit(event: SubmitEvent): void {
        if (!this.searchInput || !this.searchForm) return;

        const query = this.searchInput.value.trim();
        const validation = this.validateInput(query);

        if (!validation.isValid) {
            event.preventDefault();
            this.displayError(validation.errorMessage);
            return;
        }

        // If validation passes, allow form submission or navigate to action URL
        // The form will submit naturally with the search parameters
        // or we can manually navigate if needed
        // event.preventDefault() is NOT called here - let the form submit normally
    }

    /**
     * Handles reset button click.
     * Clears the search field, removes results, and resets all state classes.
     *
     * @protected
     * @param {MouseEvent} event - The click event
     */
    protected onReset(event: MouseEvent): void {
        event.preventDefault();

        if (!this.searchInput) return;

        // Clear input and state
        this.searchInput.value = '';
        this.currentQuery = '';
        this.clearDebounceTimer();
        this.cancelActiveSearch();
        this.clearResults();
        this.updateStateClasses();

        // Remove active state
        this.classList.remove('tk-search--active');

        // Dispatch reset event
        this.dispatchEvent(new CustomEvent('tk-search:reset'));
    }

    /**
     * Handles back button click.
     * Navigates back in browser history.
     *
     * @protected
     * @param {MouseEvent} event - The click event
     */
    protected onBack(event: MouseEvent): void {
        event.preventDefault();
        window.history.back();
    }

    /**
     * Handles click-outside event to close the search panel.
     * Removes the "active" modifier when clicking outside the component.
     *
     * @protected
     * @param {MouseEvent} event - The mouse event
     */
    protected onClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Check if click is outside this component
        if (!this.contains(target)) {
            this.classList.remove('tk-search--active');
        }
    }

    /**
     * Performs the actual search by calling the fetch endpoint with POST request.
     * Used for auto-suggest functionality to load search results asynchronously.
     * Sets loading state, builds request payload, and handles the response.
     *
     * @protected
     * @param {string} query - The search query string
     */
    protected performSearch(query: string): void {
        if (!this.resultContainer || !this.fetchUrl) return;

        const requestId = ++this.searchRequestId;
        this.searchRequestController = new AbortController();
        this.isLoading = true;
        this.updateStateClasses();
        this.dispatchEvent(new CustomEvent('tk-search:loading'));

        // Build GET URL with query parameters.
        const requestUrl = new URL(this.fetchUrl, window.location.origin);
        requestUrl.searchParams.set('query', query);

        // Add filter parameter if available.
        if (this.filterSelect && this.filterSelect.value) {
            requestUrl.searchParams.set('filter', this.filterSelect.value);
        }

        // Make fetch request as GET request (no payload).
        fetchRequest({
            requestURL: requestUrl.toString(),
            resolveHandler: (response) => {
                if (requestId === this.searchRequestId) {
                    this.handleSearchResponse(response);
                }
            },
            rejectHandler: (error: unknown) => {
                if (requestId === this.searchRequestId) {
                    this.handleSearchError(error as Error);
                }
            },
            signal: this.searchRequestController.signal,
        });
    }

    /**
     * Handles successful search response.
     * Inserts the HTML content into the result container and updates state classes.
     *
     * @protected
     * @param {TKResponse} response - The fetch response object
     */
    protected handleSearchResponse(response: TKResponse): void {
        this.isLoading = false;

        if (!response || !response.success || !this.resultContainer) {
            this.handleSearchError(new Error('Invalid response'));
            return;
        }

        this.resultContainer.innerHTML = response.dataAsHtml || '';
        const resultLists = this.resultContainer.querySelectorAll<HTMLElement>('[data-tk-search-result-list]');
        const hasData = Array.from(resultLists).some((resultList) => resultList.childElementCount > 0);

        if (hasData) {
            this.highlightSearchTerm(this.currentQuery);
            this.classList.add('tk-search--has-data');
            this.classList.remove('tk-search--no-results');
        } else {
            this.resultContainer.innerHTML = this.getNoResultsMessage(this.currentQuery);
            this.classList.add('tk-search--no-results');
            this.classList.remove('tk-search--has-data');
        }

        this.updateStateClasses();
        this.dispatchEvent(new CustomEvent('tk-search:loaded', { detail: { query: this.currentQuery } }));
    }

    /**
     * Wraps all visible occurrences of the current search query in the response.
     *
     * @protected
     * @param {string} query - The search query to highlight
     */
    protected highlightSearchTerm(query: string): void {
        if (!this.resultContainer || !query) return;

        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const searchTermMatcher = new RegExp(escapedQuery, 'i');
        const searchTermRegex = new RegExp(`(${escapedQuery})`, 'gi');
        const textNodes = document.createTreeWalker(this.resultContainer, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                const parent = node.parentElement;
                if (!node.nodeValue?.trim() || parent?.closest('script, style, .tk-search__term')) {
                    return NodeFilter.FILTER_REJECT;
                }

                return searchTermMatcher.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
            },
        });

        const matchingNodes: Text[] = [];
        let textNode = textNodes.nextNode();
        while (textNode) {
            matchingNodes.push(textNode as Text);
            textNode = textNodes.nextNode();
        }

        matchingNodes.forEach((node) => {
            const fragment = document.createDocumentFragment();
            const parts = node.nodeValue?.split(searchTermRegex) || [];

            parts.forEach((part, index) => {
                if (index % 2 === 1) {
                    const term = document.createElement('span');
                    term.className = 'tk-search__term';
                    term.textContent = part;
                    fragment.appendChild(term);
                } else if (part) {
                    fragment.appendChild(document.createTextNode(part));
                }
            });

            node.replaceWith(fragment);
        });
    }

    /**
     * Handles search request errors.
     * Displays an error message in the result container.
     *
     * @protected
     * @param {Error} error - The error object
     */
    protected handleSearchError(error: Error): void {
        this.isLoading = false;
        this.classList.add('tk-search--has-error');
        this.displayError(this.getTranslation('requestFailed'));
        this.updateStateClasses();
        this.dispatchEvent(new CustomEvent('tk-search:error', { detail: { error: error.message } }));
    }

    /**
     * Validates the search input against configured rules.
     * Checks for:
     * - Minimum input length
     * - Dangerous strings (e.g., '<' character)
     * - Allowed character pattern
     *
     * @protected
     * @param {string} query - The query string to validate
     * @returns {object} Validation result with isValid flag and optional errorMessage
     */
    protected validateInput(query: string): { isValid: boolean; key: string; errorMessage: string } {
        // Check minimum input length
        if (query.length < this.minInputLength) {
            this.classList.add('tk-search--too-short');
            return {
                isValid: false,
                key: 'inputTooShort',
                errorMessage: this.getTranslation('inputTooShort', this.minInputLength),
            };
        }

        this.classList.remove('tk-search--too-short');

        // Check for dangerous strings
        if (this.hasDangerousString(query)) {
            this.classList.add('tk-search--has-error');
            return {
                isValid: false,
                key: 'inputDangerous',
                errorMessage: this.getTranslation('inputDangerous'),
            };
        }

        // Check against allowed characters regex
        if (!this.allowedCharactersRegex.test(query)) {
            this.classList.add('tk-search--has-error');
            return {
                isValid: false,
                key: 'inputDisallowed',
                errorMessage: this.getTranslation('inputDisallowed'),
            };
        }

        this.classList.remove('tk-search--has-error');
        return { isValid: true, key: '', errorMessage: '' };
    }

    /**
     * Checks if the input contains dangerous characters like '<' (XSS prevention).
     * This is a security measure to prevent injection attacks.
     *
     * @protected
     * @param {string} query - The query string to check
     * @returns {boolean} True if dangerous characters are found
     */
    protected hasDangerousString(query: string): boolean {
        return /<|>|&lt;|&gt;|script|iframe|onerror|onload/i.test(query);
    }

    /**
     * Clears the search results and removes result-related state classes.
     *
     * @protected
     */
    protected clearResults(): void {
        if (!this.resultContainer) return;

        this.resultContainer.innerHTML = '';
        this.classList.remove('tk-search--has-data', 'tk-search--no-results', 'tk-search--has-error');
    }

    /**
     * Displays an error message in the result container.
     *
     * @protected
     * @param {string} message - The error message to display
     */
    protected displayError(message: string, type: 'warning' | 'error' | 'info' = 'warning'): void {
        if (!this.resultContainer) return;

        const errorTemplateString = this.getMessageTemplate(type, message);

        this.resultContainer.innerHTML = errorTemplateString;

        this.classList.add('tk-search--has-error');
    }

    /**
     * Returns a localized "no results" message.
     *
     * @protected
     * @param {string} query - The search query that returned no results
     * @returns {string} HTML string for "no results" message
     */
    protected getNoResultsMessage(query: string): string {
        return this.getMessageTemplate('warning', this.getTranslation('noResults', this.escapeHtml(query)));
    }

    /**
     * Gets a translated message and replaces numbered placeholders with values.
     *
     * @protected
     * @param {keyof SearchTranslations} key - Translation key
     * @param {(string | number)[]} values - Placeholder values
     * @returns {string} Localized message
     */
    protected getTranslation(key: keyof SearchTranslations, ...values: (string | number)[]): string {
        return values.reduce<string>(
            (message, value, index) => message.replaceAll(`%${index + 1}`, String(value)),
            this.translations[key] || '',
        );
    }

    /**
     * Escapes HTML special characters to prevent XSS attacks.
     *
     * @protected
     * @param {string} text - Text to escape
     * @returns {string} Escaped text
     */
    protected escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Updates all CSS state modifier classes based on current component state.
     *
     * @protected
     */
    protected updateStateClasses(): void {
        if (this.currentQuery.length > 0) {
            this.classList.add('tk-search--has-value');
        } else {
            this.classList.remove('tk-search--has-value');
        }

        if (this.isLoading) {
            this.classList.add('tk-search--is-loading');
        } else {
            this.classList.remove('tk-search--is-loading');
        }
    }

    /**
     * Clears the debounce timer if one is active.
     *
     * @protected
     */
    protected clearDebounceTimer(): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = undefined;
        }
    }

    /**
     * Aborts the pending search request and resets its loading state.
     *
     * @protected
     */
    protected cancelActiveSearch(): void {
        this.searchRequestId++;
        this.searchRequestController?.abort();
        this.searchRequestController = undefined;
        this.isLoading = false;
        this.updateStateClasses();
    }

    /**
     * Normalizes the allowed characters pattern for regex usage.
     * Removes leading '^' and surrounding brackets if present.
     *
     * @protected
     * @param {string} pattern - The allowed characters pattern
     * @returns {string} Normalized pattern
     */
    protected normalizeAllowedCharactersPattern(pattern: string): string {
        const trimmedPattern = pattern.trim();

        if (trimmedPattern.startsWith('[') && trimmedPattern.endsWith(']')) {
            return trimmedPattern.slice(1, -1).replace(/^\^/, '');
        }

        return trimmedPattern.replace(/^\^/, '');
    }

    /**
     * Returns the HTML template for a message of the specified type.
     *
     * @param type - The type of message ('info', 'warning', 'error')
     * @param message - The message text
     * @returns {string} The HTML string for the message
     */
    protected getMessageTemplate(type: 'info' | 'warning' | 'error', message: string): string {
        return `
            <div class="tk-message tk-message--${type} tk-message--breakout" role="alert">
                <span class="tk-message__icon">
                    <i class="tk-icon-${type}"></i>
                </span>
                <span class="tk-message__label">
                    ${message}
                </span>
            </div>
        `;
    }
}
